import { Show, createEffect, createSignal, type JSX, type ParentProps } from 'solid-js';
import { isDescendant, isRootId, type MaterializedTree } from '@bsync/shared';
import { ROOT_IDS, childFolders, titleOf } from '../lib/bookmarks';

export function Dialog(props: ParentProps<{ open: boolean; title: string; onClose: () => void; footer?: JSX.Element }>) {
  let el: HTMLDialogElement | undefined;
  createEffect(() => {
    if (!el) return;
    if (props.open && !el.open) el.showModal();
    if (!props.open && el.open) el.close();
  });
  return (
    <dialog ref={el} class="dialog" onClose={props.onClose} onCancel={props.onClose} onClick={(e) => e.target === el && props.onClose()}>
      <Show when={props.open}>
        <h2>{props.title}</h2>
        {props.children}
      </Show>
    </dialog>
  );
}

/** Every folder as an indented option, excluding `exclude` and their descendants. */
export function FolderSelect(props: { tree: MaterializedTree; value: string; onChange: (id: string) => void; exclude?: string[]; id?: string }) {
  const options = () => {
    const out: { id: string; label: string }[] = [];
    const excluded = (id: string) => props.exclude?.some((x) => x === id || (!isRootId(id) && isDescendant(props.tree, id, x)));
    const walk = (id: string, depth: number) => {
      if (excluded(id)) return;
      out.push({ id, label: `${' '.repeat(depth)}${titleOf(props.tree, id)}` });
      for (const c of childFolders(props.tree, id)) walk(c, depth + 1);
    };
    for (const r of ROOT_IDS) {
      if (r === 'root:toolbar' || r === 'root:other' || props.tree.childrenOf.get(r)?.length || props.value === r) walk(r, 0);
    }
    return out;
  };
  return (
    <select id={props.id} class="input" value={props.value} onChange={(e) => props.onChange(e.currentTarget.value)}>
      {options().map((o) => (
        <option value={o.id} selected={o.id === props.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export interface EditTarget {
  mode: 'create-bookmark' | 'create-folder' | 'edit';
  id?: string;
  kind: 'bookmark' | 'folder';
  parentId: string;
  title: string;
  url: string;
}

export function EditDialog(props: { target: EditTarget | null; tree: MaterializedTree | null; onClose: () => void; onSave: (t: EditTarget) => Promise<void> }) {
  const [title, setTitle] = createSignal('');
  const [url, setUrl] = createSignal('');
  const [parent, setParent] = createSignal('root:toolbar');
  const [error, setError] = createSignal<string | null>(null);
  const [saving, setSaving] = createSignal(false);

  createEffect(() => {
    const t = props.target;
    if (!t) return;
    setTitle(t.title);
    setUrl(t.url);
    setParent(t.parentId);
    setError(null);
  });

  const heading = () =>
    props.target?.mode === 'create-folder' ? 'New folder' : props.target?.mode === 'create-bookmark' ? 'New bookmark' : props.target?.kind === 'folder' ? 'Edit folder' : 'Edit bookmark';

  const submit = async (e: Event) => {
    e.preventDefault();
    const t = props.target!;
    if (t.kind === 'bookmark' && !url().trim()) {
      setError('Enter the address of the page.');
      return;
    }
    setSaving(true);
    try {
      await props.onSave({ ...t, title: title(), url: url(), parentId: parent() });
      props.onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={!!props.target} title={heading()} onClose={props.onClose}>
      <form class="form" onSubmit={submit}>
        <Show when={props.target?.kind === 'bookmark'}>
          <label class="field">
            <span>Address</span>
            <input class="input" type="text" inputmode="url" autocomplete="off" placeholder="https://example.com" value={url()} onInput={(e) => setUrl(e.currentTarget.value)} autofocus={props.target?.mode !== 'edit'} />
          </label>
        </Show>
        <label class="field">
          <span>{props.target?.kind === 'folder' ? 'Folder name' : 'Title'}</span>
          <input class="input" type="text" value={title()} onInput={(e) => setTitle(e.currentTarget.value)} placeholder={props.target?.kind === 'folder' ? 'New folder' : 'Uses the address if left empty'} maxLength={1024} autofocus={props.target?.mode === 'edit' || props.target?.kind === 'folder'} />
        </label>
        <Show when={props.tree && props.target?.mode !== 'edit'}>
          <label class="field">
            <span>Folder</span>
            <FolderSelect tree={props.tree!} value={parent()} onChange={setParent} />
          </label>
        </Show>
        <Show when={error()}>
          <p class="form-error" role="alert">
            {error()}
          </p>
        </Show>
        <div class="dialog-actions">
          <button type="button" class="btn ghost" onClick={props.onClose}>
            Cancel
          </button>
          <button type="submit" class="btn primary" disabled={saving()}>
            {props.target?.mode === 'edit' ? 'Save' : props.target?.kind === 'folder' ? 'Create folder' : 'Add bookmark'}
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function MoveDialog(props: { ids: string[] | null; tree: MaterializedTree | null; initial: string; onClose: () => void; onMove: (target: string) => Promise<void> }) {
  const [target, setTarget] = createSignal(props.initial);
  createEffect(() => props.ids && setTarget(props.initial));
  return (
    <Dialog open={!!props.ids} title={props.ids?.length === 1 ? 'Move to folder' : `Move ${props.ids?.length ?? 0} items`} onClose={props.onClose}>
      <form
        class="form"
        onSubmit={async (e) => {
          e.preventDefault();
          await props.onMove(target());
          props.onClose();
        }}
      >
        <Show when={props.tree}>
          <label class="field">
            <span>Destination</span>
            <FolderSelect tree={props.tree!} value={target()} onChange={setTarget} exclude={props.ids ?? []} />
          </label>
        </Show>
        <div class="dialog-actions">
          <button type="button" class="btn ghost" onClick={props.onClose}>
            Cancel
          </button>
          <button type="submit" class="btn primary">
            Move
          </button>
        </div>
      </form>
    </Dialog>
  );
}

export function ConfirmDialog(props: { open: boolean; title: string; body: JSX.Element; confirm: string; danger?: boolean; onClose: () => void; onConfirm: () => Promise<void> | void }) {
  const [busy, setBusy] = createSignal(false);
  return (
    <Dialog open={props.open} title={props.title} onClose={props.onClose}>
      <div class="dialog-body">{props.body}</div>
      <div class="dialog-actions">
        <button class="btn ghost" onClick={props.onClose}>
          Cancel
        </button>
        <button
          class="btn"
          classList={{ danger: props.danger, primary: !props.danger }}
          disabled={busy()}
          autofocus
          onClick={async () => {
            setBusy(true);
            try {
              await props.onConfirm();
              props.onClose();
            } finally {
              setBusy(false);
            }
          }}
        >
          {props.confirm}
        </button>
      </div>
    </Dialog>
  );
}
